/* ══════════════════════════════════════════════════════════════
   Product Card Component — Maison Flaneur Redesign
   Gallery, Quick-Add, Bottom Sheet
   ══════════════════════════════════════════════════════════════ */

(function () {
  'use strict';

  /* ── MFProductCard ── */
  class MFProductCard {
    constructor(el) {
      this.el = el;
      this.productId = el.dataset.productId;
      this.track = el.querySelector('.mf-product-card__image-track');
      this.slides = el.querySelectorAll('.mf-product-card__slide');
      this.dots = el.querySelectorAll('.mf-product-card__dot');
      this.currentIndex = 0;
      this.imageCount = this.slides.length;

      this.productData = this._parseJSON('.mf-product-card__product-json');
      this.variantData = this._parseJSON('.mf-product-card__variants-json');

      this.initGallery();
      this.initQuickAdd();
      this.initMobileAdd();
      this.initLazyLoad();
    }

    _parseJSON(selector) {
      const el = this.el.querySelector(selector);
      if (!el) return null;
      try { return JSON.parse(el.textContent); } catch (e) { return null; }
    }

    /* ── Gallery ── */
    initGallery() {
      if (this.imageCount <= 1) return;

      // Arrow clicks
      const prevBtn = this.el.querySelector('.mf-product-card__arrow--prev');
      const nextBtn = this.el.querySelector('.mf-product-card__arrow--next');
      if (prevBtn) prevBtn.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); this.goToImage(this.currentIndex - 1); });
      if (nextBtn) nextBtn.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); this.goToImage(this.currentIndex + 1); });

      // Dot clicks
      this.dots.forEach((dot) => {
        dot.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.goToImage(parseInt(dot.dataset.index, 10));
        });
      });

      // Touch swipe
      this.initSwipe();
    }

    goToImage(index) {
      if (index < 0) index = this.imageCount - 1;
      if (index >= this.imageCount) index = 0;
      this.currentIndex = index;
      this.track.style.transform = 'translateX(-' + (index * 100) + '%)';
      this.dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === index);
      });
    }

    initSwipe() {
      let startX = 0;
      let startY = 0;
      let deltaX = 0;
      let isSwiping = false;
      const media = this.el.querySelector('.mf-product-card__media');
      if (!media) return;

      media.addEventListener('touchstart', (e) => {
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
        deltaX = 0;
        isSwiping = false;
      }, { passive: true });

      media.addEventListener('touchmove', (e) => {
        deltaX = e.touches[0].clientX - startX;
        const deltaY = e.touches[0].clientY - startY;
        if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 10) {
          isSwiping = true;
        }
      }, { passive: true });

      media.addEventListener('touchend', () => {
        if (!isSwiping) return;
        if (deltaX > 40) {
          this.goToImage(this.currentIndex - 1);
        } else if (deltaX < -40) {
          this.goToImage(this.currentIndex + 1);
        }
      }, { passive: true });
    }

    /* ── Lazy load images 2+ ── */
    initLazyLoad() {
      if (this.imageCount <= 1) return;
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.slides.forEach((slide, i) => {
              if (i === 0) return;
              const img = slide.querySelector('img[loading="lazy"]');
              if (img && img.dataset.src) {
                img.src = img.dataset.src;
                delete img.dataset.src;
              }
            });
            observer.unobserve(entry.target);
          }
        });
      }, { rootMargin: '200px' });
      observer.observe(this.el);
    }

    /* ── Navigate gallery to a variant's featured image ── */
    _goToVariantImage(variantId) {
      if (!this.variantData) return;
      const variant = this.variantData.find((v) => v.id === variantId);
      if (!variant || !variant.featured_image) return;
      // Normalise: strip leading "https:" so protocol-relative "//cdn…" URLs match
      const normalize = (url) => (url || '').replace(/^https?:/, '');
      const target = normalize(variant.featured_image);
      const slideList = Array.from(this.slides);
      for (let i = 0; i < slideList.length; i++) {
        const img = slideList[i].querySelector('img');
        if (img && normalize(img.getAttribute('src') || '') === target) {
          this.goToImage(i);
          break;
        }
      }
    }

    /* ── Quick-Add (desktop) ── */
    initQuickAdd() {
      const quickAdd = this.el.querySelector('.mf-product-card__quick-add');
      if (!quickAdd) return;

      const submitBtn = quickAdd.querySelector('.mf-quickadd-submit');
      if (!submitBtn || submitBtn.classList.contains('mf-quickadd-submit--quote')) return;

      const type = quickAdd.dataset.quickAddType;

      if (type === 'variant') {
        const select = quickAdd.querySelector('.mf-variant-select');
        if (select) {
          // Prevent card navigation when interacting with select
          select.addEventListener('click', (e) => e.stopPropagation());
          select.addEventListener('change', (e) => {
            e.stopPropagation();
            submitBtn.dataset.variantId = select.value;
            // Switch card image to the variant's featured image if it has one
            this._goToVariantImage(parseInt(select.value, 10));
          });
        }
      }

      submitBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const variantId = submitBtn.dataset.variantId;
        if (variantId) this.addToCart(variantId, submitBtn);
      });
    }

    /* ── Mobile "+" button ── */
    initMobileAdd() {
      const mobileBtn = this.el.querySelector('.mf-product-card__mobile-add');
      if (!mobileBtn) return;

      mobileBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        // Always open bottom sheet (variant selection + add-to-cart)
        MFBottomSheet.open(this);
      });
    }

    /* ── Add to cart ── */
    async addToCart(variantId, btn, skipAddedState = false) {
      if (btn._adding) return;
      btn._adding = true;

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: parseInt(variantId, 10), quantity: 1 })
        });

        if (!response.ok) throw new Error('Add to cart failed');

        // Button state: checkmark + "Added" for 1.5s, then revert
        if (!skipAddedState) this.showAddedState(btn);

        // Update cart drawer content (theme convention)
        const cartResponse = await fetch('/cart.js');
        const cartData = await cartResponse.json();
        if (window.wetheme && window.wetheme.cartDrawer) {
          window.wetheme.cartDrawer.updateCartDrawer(cartData);
        } else if (window.wetheme && typeof window.wetheme.updateCartDrawer === 'function') {
          window.wetheme.updateCartDrawer(cartData);
        }

        // Open drawer — only if currently closed. Click the theme's own
        // cart toggle <button> (not an <a href="/cart">, which would navigate).
        const wrapper = document.getElementById('cartSlideoutWrapper');
        const isHidden = !wrapper || wrapper.classList.contains('slideout-panel-hidden');
        if (isHidden) {
          // Prefer a visible <button.slide-menu-cart>; fall back to any button match
          let cartToggle = null;
          const candidates = document.querySelectorAll('button.slide-menu-cart');
          for (let i = 0; i < candidates.length; i++) {
            const rect = candidates[i].getBoundingClientRect();
            if (rect.width > 0 && rect.height > 0) { cartToggle = candidates[i]; break; }
          }
          if (!cartToggle && candidates.length) cartToggle = candidates[0];

          if (cartToggle) {
            cartToggle.click();
          } else {
            // Fallbacks if no <button.slide-menu-cart> exists
            const rightSlideout = window.wetheme && window.wetheme.drawer && window.wetheme.drawer.slideouts && window.wetheme.drawer.slideouts.right;
            if (rightSlideout && typeof rightSlideout.open === 'function') {
              rightSlideout.open();
            } else if (wrapper) {
              if (window.jQuery) {
                window.jQuery(wrapper).trigger('cart:open');
              } else {
                wrapper.dispatchEvent(new CustomEvent('cart:open', { bubbles: true }));
              }
            }
          }
        }
      } catch (error) {
        console.error('MFProductCard: Add to cart error', error);
      } finally {
        btn._adding = false;
      }
    }

    showAddedState(btn) {
      const originalText = btn.dataset.originalText || btn.textContent;
      btn.classList.add('is-added');
      btn.textContent = '\u2713 Added';
      setTimeout(() => {
        btn.classList.remove('is-added');
        btn.textContent = originalText;
      }, 1500);
    }
  }

  /* ── MFBottomSheet ── */
  class MFBottomSheet {
    static el = null;
    static currentCard = null;
    static touchStartY = 0;

    static init() {
      MFBottomSheet.el = document.getElementById('mf-bottom-sheet');
      if (!MFBottomSheet.el) return;

      const backdrop = MFBottomSheet.el.querySelector('.mf-bottom-sheet__backdrop');
      const closeBtn = MFBottomSheet.el.querySelector('.mf-bottom-sheet__close');

      if (backdrop) backdrop.addEventListener('click', () => MFBottomSheet.close());
      if (closeBtn) closeBtn.addEventListener('click', () => MFBottomSheet.close());

      // Swipe down to close
      const panel = MFBottomSheet.el.querySelector('.mf-bottom-sheet__panel');
      if (panel) {
        panel.addEventListener('touchstart', (e) => {
          MFBottomSheet.touchStartY = e.touches[0].clientY;
        }, { passive: true });
        panel.addEventListener('touchend', (e) => {
          const deltaY = e.changedTouches[0].clientY - MFBottomSheet.touchStartY;
          if (deltaY > 80) MFBottomSheet.close();
        }, { passive: true });
      }
    }

    static open(card) {
      if (!MFBottomSheet.el) MFBottomSheet.init();
      if (!MFBottomSheet.el) return;

      MFBottomSheet.currentCard = card;
      const content = MFBottomSheet.el.querySelector('.mf-bottom-sheet__content');
      const title = MFBottomSheet.el.querySelector('.mf-bottom-sheet__title');
      if (!content) return;

      const product = card.productData;
      const variants = card.variantData;
      if (!product || !variants) return;

      const domTitle = card.el.querySelector('.mf-product-card__title');
      const productTitle = (product.title && product.title.trim()) || (domTitle && domTitle.textContent.trim()) || '';

      title.textContent = productTitle;

      // Build content
      let html = '';

      // Product header
      const imgSrc = product.images && product.images[0] ? (product.images[0].src || product.images[0]) : '';
      const imgUrl = typeof imgSrc === 'string' && (imgSrc.indexOf('http') === 0 || imgSrc.indexOf('//') === 0) ? imgSrc : '';
      html += '<div class="mf-bottom-sheet__product-header">';
      if (imgUrl) html += '<img class="mf-bottom-sheet__product-image" src="' + imgUrl + '" alt="' + productTitle + '">';
      html += '<div class="mf-bottom-sheet__product-info">';
      html += '<p class="mf-bottom-sheet__product-price" id="mf-bs-price">' + MFBottomSheet.formatMoney(variants[0].price) + '</p>';
      html += '</div></div>';

      // Variant selection — single select dropdown, option names shown for multi-option products
      const firstAvailable = variants.find(function (v) { return v.available; });
      const selectedId = firstAvailable ? firstAvailable.id : variants[0].id;
      const optionNames = (product.options || []).map(function (o) {
        return typeof o === 'string' ? o : (o && o.name ? o.name : '');
      });
      const isMultiOption = optionNames.length > 1;

      const hasMultipleVariants = variants.length > 1;
      if (hasMultipleVariants) {
        html += '<div class="mf-variant-pills" id="mf-bs-pills">';
        variants.forEach(function (v) {
          let label;
          if (isMultiOption) {
            const parts = [];
            for (let i = 0; i < optionNames.length; i++) {
              const val = v['option' + (i + 1)];
              if (val) parts.push(val);
            }
            label = parts.join(' / ');
          } else {
            label = v.title;
          }
          const classes = ['mf-variant-pill'];
          if (v.id === selectedId) classes.push('is-selected');
          if (!v.available) classes.push('is-unavailable');
          const imgAttr = v.featured_image ? ' data-variant-image="' + v.featured_image + '"' : '';
          html += '<button type="button" class="' + classes.join(' ') + '"' +
                  ' data-variant-id="' + v.id + '"' +
                  ' data-variant-price="' + v.price + '"' +
                  imgAttr +
                  (!v.available ? ' disabled' : '') + '>' +
                  label + '</button>';
        });
        html += '</div>';
      }

      const isTrade = card.el.dataset.isTrade === 'true';
      const submitBtnHtml = '<button class="mf-quickadd-submit" id="mf-bs-submit"' +
        (isTrade ? ' style="flex:1;height:46px;padding:0 16px;font-size:13px;box-sizing:border-box;"' : '') +
        ' data-variant-id="' + selectedId + '" data-original-text="Add to cart">Add to cart</button>';
      if (isTrade) {
        html += '<div class="mf-quickadd-row mf-quickadd-row--trade" style="display:flex;gap:8px;align-items:center;">' + submitBtnHtml + '</div>';
      } else {
        html += submitBtnHtml;
      }

      content.innerHTML = html;

      // Bind events — variant pills
      var pillsWrap = content.querySelector('#mf-bs-pills');
      if (pillsWrap) {
        pillsWrap.addEventListener('click', function (e) {
          var pill = e.target.closest('.mf-variant-pill');
          if (!pill || pill.classList.contains('is-unavailable')) return;
          pillsWrap.querySelectorAll('.mf-variant-pill').forEach(function (p) {
            p.classList.remove('is-selected');
          });
          pill.classList.add('is-selected');
          var submit = content.querySelector('#mf-bs-submit');
          submit.dataset.variantId = pill.dataset.variantId;
          var priceEl = content.querySelector('#mf-bs-price');
          if (priceEl) priceEl.textContent = MFBottomSheet.formatMoney(parseInt(pill.dataset.variantPrice, 10));
          // Update bottom-sheet product image when variant has its own image
          if (pill.dataset.variantImage) {
            var bsImg = content.querySelector('.mf-bottom-sheet__product-image');
            if (bsImg) bsImg.src = pill.dataset.variantImage;
          }
        });
      }

      var submitBtn = content.querySelector('#mf-bs-submit');
      if (submitBtn) {
        submitBtn.addEventListener('click', function () {
          var variantId = submitBtn.dataset.variantId;
          if (variantId && MFBottomSheet.currentCard) {
            // Skip "Added" state on mobile — just close the sheet and let the
            // cart drawer appear.
            MFBottomSheet.currentCard.addToCart(variantId, submitBtn, true).then(function () {
              MFBottomSheet.close();
            });
          }
        });
      }

      // Show
      MFBottomSheet.el.classList.add('is-open');
      document.body.style.overflow = 'hidden';
    }

    static close() {
      if (!MFBottomSheet.el) return;
      MFBottomSheet.el.classList.remove('is-open');
      document.body.style.overflow = '';
      MFBottomSheet.currentCard = null;
    }

    static formatMoney(cents) {
      if (typeof cents === 'string') cents = parseInt(cents, 10);
      if (isNaN(cents)) return '';
      var amount = (cents / 100).toFixed(2);
      // Detect currency symbol immediately adjacent to digits in any .money element
      var currencySymbol = '\u00A3';
      var moneyEls = document.querySelectorAll('.money');
      for (var i = 0; i < moneyEls.length; i++) {
        var text = moneyEls[i].textContent;
        var match = text.match(/([£$€¥₹])\s*[\d]/);
        if (match && match[1]) { currencySymbol = match[1]; break; }
      }
      return currencySymbol + amount;
    }
  }

  /* ── Initialize ── */
  function initProductCards() {
    document.querySelectorAll('.mf-product-card').forEach(function (el) {
      if (!el._mfCard) {
        el._mfCard = new MFProductCard(el);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initProductCards);
  } else {
    initProductCards();
  }

  // Re-init on dynamic content (Ajaxinate, section rendering)
  var observer = new MutationObserver(function (mutations) {
    var hasNew = false;
    for (var i = 0; i < mutations.length; i++) {
      if (mutations[i].addedNodes.length > 0) {
        hasNew = true;
        break;
      }
    }
    if (hasNew) initProductCards();
  });
  observer.observe(document.body, { childList: true, subtree: true });

  // Export for external use
  window.MFProductCard = MFProductCard;
  window.MFBottomSheet = MFBottomSheet;
})();
